import { skipToken, useQuery, useMutation } from '@tanstack/react-query';
import type { QueryKey } from '@tanstack/react-query';
import { useGT } from 'gt-react';
import { orpc, rpcClient, useInvalidateRpcQueryFn } from '@/api/rpc/client';
import { useToast } from '@/context/ToastContext';

// ---------------------------------------------------------------------------
// Queries
// ---------------------------------------------------------------------------

export function useLeaseableResourcesList(params?: {
  resourceType?: string;
  isActive?: boolean;
  limit?: number;
  offset?: number;
}) {
  return useQuery({
    ...orpc.leaseableResources.list.queryOptions({
      input: {
        resourceType: params?.resourceType,
        isActive: params?.isActive,
        limit: params?.limit ?? 50,
        offset: params?.offset ?? 0,
      },
    }),
  });
}

export function useLeaseableResource(resourceId: string | undefined) {
  return useQuery({
    ...orpc.leaseableResources.get.queryOptions({
      input: resourceId ? { resourceId } : skipToken,
    }),
    enabled: !!resourceId,
  });
}

export function useResourcesForAgent(agentDefinitionId: string | undefined) {
  return useQuery({
    ...orpc.leaseableResources.listForAgent.queryOptions({
      input: agentDefinitionId ? { agentDefinitionId } : skipToken,
    }),
    enabled: !!agentDefinitionId,
  });
}

export function useResourceLeases(
  resourceId: string | undefined,
  params?: { limit?: number; offset?: number },
) {
  return useQuery({
    ...orpc.leaseableResources.listLeases.queryOptions({
      input: resourceId
        ? {
            resourceId,
            limit: params?.limit ?? 50,
            offset: params?.offset ?? 0,
          }
        : skipToken,
    }),
    enabled: !!resourceId,
    refetchInterval: 10_000,
  });
}

// ---------------------------------------------------------------------------
// Mutations
// ---------------------------------------------------------------------------

type ToastCopy = { title: string; message: string };

/**
 * The scaffold every leaseable-resource mutation shares: run the call, invalidate the query keys the
 * caller names, optionally announce success, then hand back to the caller's own `onSuccess`. Errors
 * always toast, preferring the server's message and falling back to `errorFallback` when it is empty.
 */
function useLeaseableResourceMutation<TInput, TOutput>({
  mutationFn,
  invalidate,
  success,
  errorTitle,
  errorFallback,
  onSuccess,
}: {
  mutationFn: (input: TInput) => Promise<TOutput>;
  invalidate: (o: typeof orpc) => QueryKey[];
  /** Omit to stay silent on success — linking and unlinking report through the surrounding UI. */
  success?: ToastCopy;
  errorTitle: string;
  errorFallback: string;
  onSuccess?: () => void;
}) {
  const invalidateQueries = useInvalidateRpcQueryFn(invalidate);
  const { showToast } = useToast();

  return useMutation({
    mutationFn,
    onSuccess: () => {
      if (success) {
        showToast({ ...success, color: 'success' });
      }
      invalidateQueries({});
      onSuccess?.();
    },
    onError: (error: Error) => {
      showToast({
        title: errorTitle,
        message: error.message || errorFallback,
        color: 'error',
      });
    },
  });
}

type CreateInput = Parameters<typeof rpcClient.leaseableResources.create>[0];

export function useCreateLeaseableResource(options?: {
  onSuccess?: () => void;
}) {
  return useLeaseableResourceMutation({
    mutationFn: (input: CreateInput) =>
      rpcClient.leaseableResources.create(input),
    invalidate: (o) => [o.leaseableResources.list.key()],
    success: {
      title: 'Resource created',
      message: 'Leaseable resource created successfully.',
    },
    errorTitle: 'Error',
    errorFallback: 'Failed to create resource',
    onSuccess: options?.onSuccess,
  });
}

type UpdateInput = Parameters<typeof rpcClient.leaseableResources.update>[0];

export function useUpdateLeaseableResource(options?: {
  onSuccess?: () => void;
}) {
  return useLeaseableResourceMutation({
    mutationFn: (input: UpdateInput) =>
      rpcClient.leaseableResources.update(input),
    invalidate: (o) => [o.leaseableResources.list.key()],
    success: {
      title: 'Resource updated',
      message: 'Leaseable resource updated successfully.',
    },
    errorTitle: 'Error',
    errorFallback: 'Failed to update resource',
    onSuccess: options?.onSuccess,
  });
}

type ResumeInput = Parameters<typeof rpcClient.leaseableResources.resume>[0];

/**
 * Lift the pause an operator-reported credential rejection put on a resource. Invalidates the
 * agent-definition list as well as the settings list: the per-agent tab shows the same pause block
 * and would otherwise keep rendering a resource as paused after it was resumed elsewhere.
 */
export function useResumeLeaseableResource(options?: {
  onSuccess?: () => void;
}) {
  const gt = useGT();

  return useLeaseableResourceMutation({
    mutationFn: async (input: ResumeInput) => {
      const resource = await rpcClient.leaseableResources.resume(input);
      // The handler answers null for a resource that no longer exists in the org; that is a
      // failed resume, not a "Resource resumed" toast.
      if (!resource) {
        throw new Error(
          gt('Resource not found', {
            context:
              'error when a paused agent resource cannot be resumed because it no longer exists',
          }),
        );
      }
      return resource;
    },
    invalidate: (o) => [
      o.leaseableResources.list.key(),
      o.leaseableResources.listForAgent.key(),
    ],
    success: {
      title: gt('Resource resumed', {
        context: 'toast title after resuming a paused agent resource',
      }),
      message: gt('Agents can lease this resource again.', {
        context: 'toast message after resuming a paused agent resource',
      }),
    },
    errorTitle: gt('Error', {
      context: 'toast title when resuming an agent resource fails',
    }),
    errorFallback: gt('Failed to resume resource', {
      context: 'toast message when resuming an agent resource fails',
    }),
    onSuccess: options?.onSuccess,
  });
}

type ReleaseRunLeasesInput = Parameters<
  typeof rpcClient.leaseableResources.releaseRunLeases
>[0];

/**
 * Return the leases a finished run still holds, so agents waiting on the same
 * slot can start before the lease TTL frees it. Also invalidates every agent
 * page's start-readiness query: that is where the held slot was reported, and
 * it must stop saying "held" the moment the slot is free.
 */
export function useReleaseRunLeases(options?: { onSuccess?: () => void }) {
  const gt = useGT();

  return useLeaseableResourceMutation({
    mutationFn: (input: ReleaseRunLeasesInput) =>
      rpcClient.leaseableResources.releaseRunLeases(input),
    invalidate: (o) => [
      o.leaseableResources.list.key(),
      o.leaseableResources.listForAgent.key(),
      o.leaseableResources.listLeases.key(),
      ['agent-definitions'],
    ],
    success: {
      title: gt('Resource released', {
        context: 'toast title after releasing the leases a finished run held',
      }),
      message: gt('Waiting agents can lease the resource again.', {
        context: 'toast message after releasing the leases a finished run held',
      }),
    },
    errorTitle: gt('Error', {
      context: 'toast title when releasing a finished run’s leases fails',
    }),
    errorFallback: gt('Failed to release the resource', {
      context: 'toast message when releasing a finished run’s leases fails',
    }),
    onSuccess: options?.onSuccess,
  });
}

type DeleteInput = Parameters<typeof rpcClient.leaseableResources.delete>[0];

export function useDeleteLeaseableResource(options?: {
  onSuccess?: () => void;
}) {
  return useLeaseableResourceMutation({
    mutationFn: (input: DeleteInput) =>
      rpcClient.leaseableResources.delete(input),
    invalidate: (o) => [o.leaseableResources.list.key()],
    success: {
      title: 'Resource deleted',
      message: 'Leaseable resource deleted.',
    },
    errorTitle: 'Error',
    errorFallback: 'Failed to delete resource',
    onSuccess: options?.onSuccess,
  });
}

type LinkInput = Parameters<typeof rpcClient.leaseableResources.linkAgent>[0];

export function useLinkResourceToAgent(options?: { onSuccess?: () => void }) {
  return useLeaseableResourceMutation({
    mutationFn: (input: LinkInput) =>
      rpcClient.leaseableResources.linkAgent(input),
    invalidate: (o) => [
      o.leaseableResources.list.key(),
      o.leaseableResources.listForAgent.key(),
    ],
    errorTitle: 'Error',
    errorFallback: 'Failed to link resource',
    onSuccess: options?.onSuccess,
  });
}

type UnlinkInput = Parameters<
  typeof rpcClient.leaseableResources.unlinkAgent
>[0];

export function useUnlinkResourceFromAgent(options?: {
  onSuccess?: () => void;
}) {
  return useLeaseableResourceMutation({
    mutationFn: (input: UnlinkInput) =>
      rpcClient.leaseableResources.unlinkAgent(input),
    invalidate: (o) => [
      o.leaseableResources.list.key(),
      o.leaseableResources.listForAgent.key(),
    ],
    errorTitle: 'Error',
    errorFallback: 'Failed to unlink resource',
    onSuccess: options?.onSuccess,
  });
}
